import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBusinessStore } from '@/stores/useBusinessStore';
import {
    ArrowLeft, Edit, Trash2, Package, AlertTriangle,
    TrendingUp, Box, Database, Loader2, Barcode
} from 'lucide-react';

export const ProductDetails: React.FC = () => {
    const { businessId, productId } = useParams();
    const navigate = useNavigate();
    const {
        activeBusiness,
        products,
        fetchProducts,
        deleteProduct,
        loading
    } = useBusinessStore();

    const [product, setProduct] = useState<any>(null);

    useEffect(() => {
        if (!activeBusiness) return;

        // Ensure products are loaded
        if (products.length === 0) {
            fetchProducts(activeBusiness.id);
        }
    }, [activeBusiness, products.length, fetchProducts]);

    useEffect(() => {
        if (products.length > 0 && productId) {
            const found = products.find(p => p.id === productId);
            setProduct(found || null);
        }
    }, [products, productId]);

    const handleDelete = async () => {
        if (!activeBusiness || !product) return;
        if (confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
            await deleteProduct(activeBusiness.id, product.id);
            navigate(`/business/${businessId}/products`);
        }
    };

    if (loading && !product) {
        return (
            <div className="flex justify-center items-center h-96">
                <Loader2 size={48} className="animate-spin text-primary" />
            </div>
        );
    }

    if (!product) {
        return (
            <div className="flex flex-col items-center justify-center h-96 text-center">
                <AlertTriangle size={64} className="text-yellow-500 mb-4" />
                <h2 className="text-2xl font-bold text-light-text dark:text-dark-text dark:text-white">Product Not Found</h2>
                <p className="text-light-subtle dark:text-dark-subtle mt-2">The product you are looking for does not exist or has been removed.</p>
                <button
                    onClick={() => navigate(`/business/${businessId}/products`)}
                    className="mt-6 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                    Back to Products
                </button>
            </div>
        );
    }

    const stock = product.quantity_in_stock || 0;
    const threshold = product.low_stock_threshold || 5;

    let stockStatus = { label: 'In Stock', color: 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400' };
    if (stock === 0) {
        stockStatus = { label: 'Out of Stock', color: 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400' };
    } else if (stock <= threshold) {
        stockStatus = { label: 'Low Stock', color: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400' };
    }

    return (
        <div className="max-w-[1044px] mx-auto space-y-6">
            {/* Header Actions */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(`/business/${businessId}/products`)}
                        className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                        <ArrowLeft size={24} className="text-light-subtle dark:text-dark-subtle dark:text-gray-300" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-light-text dark:text-dark-text dark:text-white flex items-center gap-3">
                            {product.name}
                            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${stockStatus.color}`}>
                                {stockStatus.label}
                            </span>
                        </h1>
                        <p className="text-sm text-light-subtle dark:text-dark-subtle dark:text-gray-400 mt-1">{product.category}</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(`/business/${businessId}/products/edit/${product.id}`)}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                        <Edit size={18} />
                        Edit
                    </button>
                    <button
                        onClick={handleDelete}
                        className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/30 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                    >
                        <Trash2 size={18} />
                        Delete
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* Left Column: Images & Primary Details */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Media Gallery */}
                    <div className="bg-light-card dark:bg-dark-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border p-6 shadow-sm">
                        <h3 className="text-lg font-semibold text-light-text dark:text-dark-text dark:text-white mb-4">Media</h3>
                        {product.media_urls && product.media_urls.length > 0 ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {product.media_urls.map((url: string, index: number) => (
                                    <div key={index} className="aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                                        <img src={url} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-48 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700">
                                <div className="text-center text-gray-400">
                                    <Package size={48} className="mx-auto mb-2 opacity-50" />
                                    <p>No images available for this product</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    <div className="bg-light-card dark:bg-dark-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border p-6 shadow-sm">
                        <h3 className="text-lg font-semibold text-light-text dark:text-dark-text dark:text-white mb-4">Description</h3>
                        <p className="text-light-subtle dark:text-dark-subtle dark:text-gray-400 whitespace-pre-wrap leading-relaxed">
                            {product.description || "No description provided for this product."}
                        </p>
                    </div>
                </div>

                {/* Right Column: Pricing, Inventory, & Metadata */}
                <div className="space-y-6">

                    {/* Pricing Card */}
                    <div className="bg-light-card dark:bg-dark-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border p-6 shadow-sm">
                        <h3 className="text-lg font-semibold text-light-text dark:text-dark-text dark:text-white mb-4 flex items-center gap-2">
                            <TrendingUp size={20} className="text-primary" />
                            Pricing
                        </h3>
                        <div className="space-y-4">
                            <div>
                                <p className="text-sm text-light-subtle dark:text-dark-subtle dark:text-gray-400 mb-1">Selling Price</p>
                                <p className="text-3xl font-bold text-light-text dark:text-dark-text dark:text-white">
                                    KES {product.price?.toLocaleString()}
                                </p>
                            </div>
                            {product.cost_price && (
                                <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
                                    <p className="text-sm text-light-subtle dark:text-dark-subtle dark:text-gray-400 mb-1">Cost Price</p>
                                    <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
                                        KES {product.cost_price?.toLocaleString()}
                                    </p>
                                </div>
                            )}
                            {product.price && product.cost_price && (
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-light-subtle dark:text-dark-subtle dark:text-gray-400">Profit Margin</span>
                                    <span className="font-semibold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded">
                                        {((product.price - product.cost_price) / product.cost_price * 100).toFixed(1)}%
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Inventory Card */}
                    <div className="bg-light-card dark:bg-dark-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border p-6 shadow-sm">
                        <h3 className="text-lg font-semibold text-light-text dark:text-dark-text dark:text-white mb-4 flex items-center gap-2">
                            <Database size={20} className="text-blue-500" />
                            Inventory
                        </h3>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
                                <p className="text-sm text-light-subtle dark:text-dark-subtle dark:text-gray-400 mb-1 flex items-center gap-1">
                                    <Box size={14} /> Total Stock
                                </p>
                                <p className="text-xl font-bold text-light-text dark:text-dark-text dark:text-white">
                                    {stock}
                                </p>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
                                <p className="text-sm text-light-subtle dark:text-dark-subtle dark:text-gray-400 mb-1 flex items-center gap-1">
                                    <AlertTriangle size={14} /> Low Alert
                                </p>
                                <p className="text-xl font-bold text-light-text dark:text-dark-text dark:text-white">
                                    {threshold}
                                </p>
                            </div>
                        </div>

                        <div className="mt-4 space-y-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                            <div className="flex justify-between text-sm">
                                <span className="text-light-subtle dark:text-dark-subtle dark:text-gray-400">SKU</span>
                                <span className="font-medium text-light-text dark:text-dark-text dark:text-gray-200">
                                    {product.sku || 'Not provided'}
                                </span>
                            </div>
                            <div className="flex justify-between text-sm items-center">
                                <span className="text-light-subtle dark:text-dark-subtle dark:text-gray-400 flex items-center gap-1">
                                    <Barcode size={14} /> Barcode
                                </span>
                                <span className="font-medium bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-light-text dark:text-dark-text dark:text-gray-200 font-mono text-xs">
                                    {product.barcode || 'Generated on scan'}
                                </span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};
